import React, { useState } from 'react';
import { X, Printer } from 'lucide-react';

// Aisle categories for grouping ingredients
const AISLE_CATEGORIES = {
  produce: {
    label: '🥬 Produce',
    keywords: ['lettuce', 'spinach', 'kale', 'arugula', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'celery', 'onion', 'garlic', 'ginger', 'potato', 'tomato', 'pepper', 'bell pepper', 'jalapeño', 'cucumber', 'zucchini', 'squash', 'eggplant', 'mushroom', 'corn', 'peas', 'beans', 'asparagus', 'artichoke', 'avocado', 'lemon', 'lime', 'orange', 'apple', 'banana', 'berry', 'berries', 'strawberry', 'blueberry', 'raspberry', 'grape', 'melon', 'watermelon', 'mango', 'pineapple', 'peach', 'pear', 'plum', 'cherry', 'fig', 'pomegranate', 'herbs', 'basil', 'cilantro', 'parsley', 'mint', 'dill', 'thyme', 'rosemary', 'sage', 'oregano', 'chives', 'scallion', 'green onion', 'leek', 'shallot', 'fennel', 'radish', 'beet', 'turnip', 'sweet potato', 'yam']
  },
  dairy: {
    label: '🧊 Dairy & Refrigerated',
    keywords: ['milk', 'cream', 'half-and-half', 'butter', 'cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'goat cheese', 'ricotta', 'cottage cheese', 'cream cheese', 'sour cream', 'yogurt', 'greek yogurt', 'egg', 'eggs', 'mayonnaise', 'mayo', 'tofu', 'tempeh']
  },
  meat: {
    label: '🥩 Meat & Seafood',
    keywords: ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'bacon', 'sausage', 'ham', 'prosciutto', 'salami', 'pepperoni', 'ground beef', 'ground turkey', 'ground pork', 'steak', 'roast', 'ribs', 'chop', 'tenderloin', 'breast', 'thigh', 'wing', 'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'halibut', 'trout', 'shrimp', 'prawn', 'crab', 'lobster', 'scallop', 'mussel', 'clam', 'oyster', 'squid', 'calamari', 'anchovy', 'anchovies', 'sardine']
  },
  bakery: {
    label: '🍞 Bakery & Bread',
    keywords: ['bread', 'loaf', 'baguette', 'roll', 'bun', 'croissant', 'bagel', 'pita', 'tortilla', 'wrap', 'naan', 'flatbread', 'crouton', 'breadcrumb', 'panko']
  },
  pantry: {
    label: '🫙 Pantry & Dry Goods',
    keywords: ['flour', 'sugar', 'brown sugar', 'powdered sugar', 'baking soda', 'baking powder', 'yeast', 'cornstarch', 'cocoa', 'chocolate', 'vanilla', 'extract', 'rice', 'pasta', 'noodle', 'spaghetti', 'penne', 'macaroni', 'quinoa', 'couscous', 'oat', 'oatmeal', 'cereal', 'granola', 'nut', 'almond', 'walnut', 'pecan', 'cashew', 'peanut', 'pistachio', 'seed', 'sesame', 'sunflower', 'chia', 'flax', 'lentil', 'chickpea', 'bean', 'black bean', 'kidney bean', 'cannellini', 'navy bean', 'pinto bean', 'canned', 'tomato paste', 'tomato sauce', 'diced tomato', 'crushed tomato', 'coconut milk', 'broth', 'stock', 'bouillon', 'honey', 'maple syrup', 'molasses', 'agave', 'jam', 'jelly', 'preserves', 'peanut butter', 'almond butter', 'tahini']
  },
  condiments: {
    label: '🍶 Oils, Vinegars & Condiments',
    keywords: ['oil', 'olive oil', 'vegetable oil', 'canola oil', 'coconut oil', 'sesame oil', 'avocado oil', 'vinegar', 'balsamic', 'red wine vinegar', 'white wine vinegar', 'apple cider vinegar', 'rice vinegar', 'soy sauce', 'tamari', 'fish sauce', 'worcestershire', 'hot sauce', 'sriracha', 'ketchup', 'mustard', 'dijon', 'relish', 'salsa', 'barbecue sauce', 'teriyaki', 'hoisin', 'oyster sauce', 'miso', 'curry paste', 'harissa', 'pesto']
  },
  spices: {
    label: '🧂 Spices & Seasonings',
    keywords: ['spice', 'seasoning', 'cumin', 'coriander', 'paprika', 'smoked paprika', 'cayenne', 'chili powder', 'curry powder', 'garam masala', 'turmeric', 'cinnamon', 'nutmeg', 'clove', 'allspice', 'cardamom', 'ginger powder', 'garlic powder', 'onion powder', 'oregano dried', 'basil dried', 'thyme dried', 'rosemary dried', 'bay leaf', 'bay leaves', 'italian seasoning', 'herbs de provence', 'za\'atar', 'sumac', 'saffron', 'vanilla bean']
  },
  frozen: {
    label: '❄️ Frozen',
    keywords: ['frozen', 'ice cream', 'sorbet', 'frozen vegetable', 'frozen fruit', 'frozen pizza', 'frozen meal']
  },
  beverages: {
    label: '🥤 Beverages',
    keywords: ['water', 'juice', 'orange juice', 'apple juice', 'lemonade', 'soda', 'wine', 'beer', 'liquor', 'vodka', 'rum', 'whiskey', 'bourbon', 'tequila', 'gin', 'brandy', 'coffee', 'tea', 'espresso']
  }
};

// Common staples that most people already have (pantry basics only)
const STAPLES = [
  'salt', 'black pepper', 'white pepper', 'kosher salt', 'sea salt',
  'olive oil', 'vegetable oil', 'canola oil', 'cooking spray',
  'flour', 'all-purpose flour', 'bread flour',
  'sugar', 'granulated sugar', 'brown sugar', 'powdered sugar',
  'baking soda', 'baking powder', 'cornstarch',
  'garlic powder', 'onion powder', 'paprika', 'cumin', 'cinnamon', 'oregano', 'basil dried', 'italian seasoning',
  'vanilla extract', 'vanilla',
  'soy sauce', 'worcestershire'
];

// Words that indicate it's NOT a staple (even if it contains a staple word)
const STAPLE_EXCLUDERS = ['bell', 'red', 'green', 'yellow', 'orange', 'jalapeño', 'cayenne', 'chili', 'hot'];

// Priority order for checking aisles (processed products before raw ingredients)
const AISLE_PRIORITY = ['dairy', 'condiments', 'spices', 'pantry', 'meat', 'bakery', 'frozen', 'beverages', 'produce'];

// Categorize an ingredient into an aisle
const categorizeIngredient = (ingredientName) => {
  const nameLower = ingredientName.toLowerCase();
  
  // Check aisle categories in priority order (dairy/condiments before produce)
  for (const aisle of AISLE_PRIORITY) {
    const config = AISLE_CATEGORIES[aisle];
    if (!config) continue;
    for (const keyword of config.keywords) {
      if (nameLower.includes(keyword)) {
        return aisle;
      }
    }
  }
  
  // Check if it's a staple - but exclude if it has excluder words (like "bell pepper")
  const hasExcluder = STAPLE_EXCLUDERS.some(exc => nameLower.includes(exc));
  if (!hasExcluder) {
    for (const staple of STAPLES) {
      if (nameLower === staple || 
          nameLower.startsWith(staple + ' ') || 
          nameLower.endsWith(' ' + staple) ||
          nameLower.includes(' ' + staple + ' ')) {
        return 'staples';
      }
    }
  }
  
  return 'other';
};

const PrintModal = ({ recipe, scale, scaleQuantity, onClose }) => {
  const [printMode, setPrintMode] = useState('recipe'); // 'recipe' or 'shopping'

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

    if (printMode === 'recipe') {
      // Recipe view - static print
      const ingredientsList = recipe.ingredients?.map(ing => {
        const qty = scaleQuantity(ing.quantity, scale);
        const hasQty = qty && qty !== 'null' && qty.trim() !== '';
        const hasUnit = ing.unit && ing.unit !== 'null' && ing.unit.trim() !== '';
        const qtyUnit = [hasQty ? qty : '', hasUnit ? ing.unit : ''].filter(Boolean).join(' ');
        const prep = ing.preparation ? ` (${ing.preparation})` : '';
        return `<li>${qtyUnit ? `<strong>${qtyUnit}</strong> ` : ''}${ing.name}${prep}</li>`;
      }).join('') || '';

      const instructionsList = recipe.instructions?.map(inst => {
        const duration = inst.duration_minutes ? ` <span style="color: #666; font-size: 0.9em;">(${inst.duration_minutes} min)</span>` : '';
        return `<li><strong>${inst.step_number}.</strong> ${inst.text}${duration}</li>`;
      }).join('') || '';

      const tipsList = recipe.recipe_tips?.map(tip => `<li>${tip}</li>`).join('') || '';

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${recipe.title} - Recipe</title>
          <style>
            body { font-family: ${fontFamily}; max-width: 1000px; margin: 0 auto; padding: 40px 20px; color: #333; font-size: 14px; }
            h1 { font-size: 22px; margin-bottom: 8px; color: #222; border-bottom: 2px solid #d97706; padding-bottom: 8px; }
            .description { font-style: italic; color: #666; margin-bottom: 16px; }
            .meta { display: flex; gap: 20px; margin-bottom: 24px; font-size: 14px; color: #555; flex-wrap: wrap; }
            .meta span { display: flex; align-items: center; gap: 4px; }
            .recipe-grid { display: grid; grid-template-columns: 35% 65%; gap: 40px; }
            .ingredients-col h2, .instructions-col h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-top: 0; margin-bottom: 16px; color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 8px; }
            ul { padding-left: 20px; line-height: 1.8; margin: 0; list-style-type: disc; }
            ol { padding-left: 0; line-height: 1.8; margin: 0; list-style-type: none; }
            ul li { margin-bottom: 8px; }
            ol li { margin-bottom: 16px; }
            .tips { background: #fef3c7; padding: 20px; margin-top: 16px; border-radius: 8px; }
            .tips h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-top: 0; margin-bottom: 12px; color: #d97706; border: none; padding: 0; }
            .tips ul { margin: 0; }
            .notes { margin-top: 20px; padding: 16px; border: 1px dashed #d97706; border-radius: 8px; }
            .notes h2 { margin-top: 0; font-size: 14px; color: #d97706; }
            .scale-note { font-size: 12px; color: #888; margin-bottom: 16px; }
            /* Floating return button */
            .return-btn { 
              position: fixed; 
              bottom: 20px; 
              right: 20px; 
              background: linear-gradient(135deg, #f59e0b, #d97706); 
              color: white; 
              border: none; 
              padding: 12px 20px; 
              border-radius: 50px; 
              font-size: 14px; 
              font-weight: 600; 
              cursor: pointer; 
              box-shadow: 0 4px 15px rgba(217, 119, 6, 0.4); 
              display: flex; 
              align-items: center; 
              gap: 8px;
              transition: transform 0.2s, box-shadow 0.2s;
              z-index: 9999;
            }
            .return-btn:hover { 
              transform: translateY(-2px); 
              box-shadow: 0 6px 20px rgba(217, 119, 6, 0.5); 
            }
            @media print { 
              body { padding: 20px; max-width: 100%; } 
              .recipe-grid { grid-template-columns: 35% 65%; }
              .return-btn { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${recipe.title}</h1>
          ${recipe.description ? `<p class="description">${recipe.description}</p>` : ''}
          <div class="meta">
            ${recipe.servings ? `<span>🍽️ ${Math.round(recipe.servings * scale)} servings</span>` : ''}
            ${recipe.prep_time ? `<span>⏱️ Prep: ${recipe.prep_time} min</span>` : ''}
            ${recipe.cook_time ? `<span>🔥 Cook: ${recipe.cook_time} min</span>` : ''}
          </div>
          ${scale !== 1 ? `<p class="scale-note">Scaled to ${scale}x original recipe</p>` : ''}
          
          <div class="recipe-grid">
            <div class="ingredients-col">
              <h2>Ingredients</h2>
              <ul>${ingredientsList}</ul>
            </div>
            <div class="instructions-col">
              <h2>Preparation</h2>
              <ol>${instructionsList}</ol>
              ${tipsList ? `<div class="tips"><h2>Tips & Tricks</h2><ul>${tipsList}</ul></div>` : ''}
            </div>
          </div>
          ${recipe.notes ? `<div class="notes"><h2>My Notes</h2><p>${recipe.notes}</p></div>` : ''}
          
          <button class="return-btn" onclick="window.close();">
            🍯 Return to Honey
          </button>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    } else {
      // Shopping list - INTERACTIVE print window
      const groups = {
        staples: [],
        produce: [],
        dairy: [],
        deli: [],
        meat: [],
        bakery: [],
        pantry: [],
        condiments: [],
        spices: [],
        frozen: [],
        beverages: [],
        other: []
      };
      
      recipe.ingredients?.forEach((ing, idx) => {
        // Use AI-assigned aisle if available (convert to lowercase), otherwise fall back to keyword matching
        const storedAisle = (ing.aisle || '').toLowerCase();
        const category = storedAisle && groups[storedAisle] !== undefined 
          ? storedAisle 
          : categorizeIngredient(ing.name);
        const qty = scaleQuantity(ing.quantity, scale);
        const hasQty = qty && qty !== 'null' && qty.trim() !== '';
        const hasUnit = ing.unit && ing.unit !== 'null' && ing.unit.trim() !== '';
        const qtyUnit = [hasQty ? qty : '', hasUnit ? ing.unit : ''].filter(Boolean).join(' ');
        groups[category].push({ name: ing.name, qtyUnit, idx });
      });

      // Build shopping list HTML with interactive checkboxes
      let shoppingHtml = '';
      const aisleOrder = ['produce', 'dairy', 'deli', 'meat', 'bakery', 'pantry', 'condiments', 'spices', 'frozen', 'beverages', 'other', 'staples'];
      
      for (const aisle of aisleOrder) {
        const items = groups[aisle];
        if (!items || items.length === 0) continue;
        
        const aisleLabel = aisle === 'staples' 
          ? '🏠 You May Already Have' 
          : aisle === 'deli'
            ? '🥪 Deli'
            : aisle === 'other' 
              ? '📦 Other Items' 
              : AISLE_CATEGORIES[aisle]?.label || aisle;
        
        const itemsHtml = items.map(ing => `
          <li class="item" data-idx="${ing.idx}">
            <label>
              <input type="checkbox" onchange="toggleItem(this)">
              <span class="item-text">${ing.qtyUnit ? `${ing.qtyUnit} ` : ''}${ing.name}</span>
            </label>
          </li>
        `).join('');
        
        shoppingHtml += `
          <div class="aisle-section">
            <h3>${aisleLabel}</h3>
            <ul>${itemsHtml}</ul>
          </div>
        `;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${recipe.title} - Shopping List</title>
          <style>
            body { font-family: ${fontFamily}; max-width: 600px; margin: 0 auto; padding: 20px; color: #333; font-size: 14px; }
            h1 { font-size: 24px; margin-bottom: 2px; color: #222; }
            h2 { font-size: 18px; color: #d97706; margin-bottom: 12px; font-weight: normal; }
            .instructions { background: #fff8e7; border: 1px solid #fcd34d; padding: 8px 12px; border-radius: 6px; margin-bottom: 16px; font-size: 13px; color: #92400e; }
            .instructions strong { color: #d97706; }
            .aisle-section { margin-bottom: 12px; }
            .aisle-section h3 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px; color: #d97706; border-bottom: 1px solid #fcd34d; padding-bottom: 3px; margin-bottom: 6px; }
            ul { padding-left: 0; line-height: 1.4; margin: 0; list-style-type: none; }
            li.item { display: flex; align-items: center; padding: 2px 4px; border-radius: 3px; cursor: pointer; font-size: 14px; }
            li.item:hover { background: #fef3c7; }
            li.item label { display: flex; align-items: center; gap: 6px; cursor: pointer; width: 100%; }
            li.item input[type="checkbox"] { width: 14px; height: 14px; accent-color: #d97706; cursor: pointer; }
            li.item.excluded .item-text { text-decoration: line-through; color: #999; }
            .scale-note { font-size: 10px; color: #888; margin-bottom: 10px; }
            .aisle-section.all-excluded h3 { color: #ccc; }
            @media print { 
              body { padding: 10px; max-width: 100%; font-size: 14px; }
              h1 { font-size: 20px; }
              h2 { font-size: 18px; margin-bottom: 8px; }
              .instructions { display: none; }
              .aisle-section { margin-bottom: 8px; margin-top: 12px; }
              .aisle-section:first-of-type { margin-top: 0; }
              .aisle-section h3 { font-size: 14px; margin-bottom: 4px; padding-bottom: 2px; }
              ul { line-height: 1.3; }
              li.item { padding: 1px 2px; font-size: 14px; }
              li.item:hover { background: none; }
              li.item input[type="checkbox"] { display: none; }
              li.item::before { content: "☐"; font-size: 16px; margin-right: 5px; }
              li.item.excluded { display: none; }
              .aisle-section.all-excluded { display: none; }
              .return-btn { display: none; }
            }
            /* Floating return button */
            .return-btn { 
              position: fixed; 
              bottom: 20px; 
              right: 20px; 
              background: linear-gradient(135deg, #f59e0b, #d97706); 
              color: white; 
              border: none; 
              padding: 12px 20px; 
              border-radius: 50px; 
              font-size: 14px; 
              font-weight: 600; 
              cursor: pointer; 
              box-shadow: 0 4px 15px rgba(217, 119, 6, 0.4); 
              display: flex; 
              align-items: center; 
              gap: 8px;
              transition: transform 0.2s, box-shadow 0.2s;
              z-index: 9999;
            }
            .return-btn:hover { 
              transform: translateY(-2px); 
              box-shadow: 0 6px 20px rgba(217, 119, 6, 0.5); 
            }
          </style>
        </head>
        <body>
          <h1>🛒 Shopping List</h1>
          <h2>${recipe.title}${scale !== 1 ? ` (${scale}x)` : ''}</h2>
          
          <div class="instructions">
            <strong>📝 Interactive!</strong> Check items you already have to strike them through. Then press <strong>Ctrl+P</strong> (or ⌘+P on Mac) to print. Checked items will be hidden from the printout.
          </div>
          
          ${scale !== 1 ? `<p class="scale-note">Quantities scaled to ${scale}x original recipe</p>` : ''}
          ${shoppingHtml}
          
          <button class="return-btn" onclick="window.close();">
            🍯 Return to Honey
          </button>
          
          <script>
            function toggleItem(checkbox) {
              const li = checkbox.closest('li.item');
              if (checkbox.checked) {
                li.classList.add('excluded');
              } else {
                li.classList.remove('excluded');
              }
              updateSectionVisibility();
            }
            
            function updateSectionVisibility() {
              // Check each section - if all items are excluded, hide the section
              document.querySelectorAll('.aisle-section').forEach(section => {
                const items = section.querySelectorAll('li.item');
                const allExcluded = Array.from(items).every(item => item.classList.contains('excluded'));
                if (allExcluded) {
                  section.classList.add('all-excluded');
                } else {
                  section.classList.remove('all-excluded');
                }
              });
            }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
    
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-amber-600 p-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Printer size={20} />
              <h3 className="text-lg font-bold">Print Options</h3>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="space-y-3">
            <label 
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                printMode === 'recipe' 
                  ? 'border-amber-600 bg-amber-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="printMode"
                value="recipe"
                checked={printMode === 'recipe'}
                onChange={() => setPrintMode('recipe')}
                className="w-4 h-4 accent-amber-600"
              />
              <span className="font-medium">Recipe View</span>
            </label>
            
            <label 
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                printMode === 'shopping' 
                  ? 'border-amber-600 bg-amber-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="printMode"
                value="shopping"
                checked={printMode === 'shopping'}
                onChange={() => setPrintMode('shopping')}
                className="w-4 h-4 accent-amber-600"
              />
              <span className="font-medium">Shopping List (by aisle)</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center gap-2"
          >
            <Printer size={18} />
            Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintModal;
